import { isLongPressExcludedTarget } from "./isLongPressExcludedTarget";
import { longPressDebug } from "./longPressDebug";

export const LONG_PRESS_DURATION_MS = 750;
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

function findTouch(touches, id) {
  for (let i = 0; i < touches.length; i++) {
    if (touches[i].identifier === id) {
      return touches[i];
    }
  }
  return null;
}

/**
 * Long-press and right-click handlers for opening the Dashboard Navigator.
 * Cancels when movement exceeds tolerance so scroll and swipe are preserved.
 *
 * @param {object} options
 * @param {() => void} options.onLongPress
 * @param {number} [options.durationMs]
 * @param {number} [options.tolerancePx]
 * @param {(target: EventTarget | null) => boolean} [options.isExcludedTarget]
 * @param {boolean} [options.preventDefaultOnStart] When true, suppresses iOS callout on dedicated controls (not scroll surfaces).
 */
export function createCarouselLongPressHandlers({
  onLongPress,
  durationMs = LONG_PRESS_DURATION_MS,
  tolerancePx = LONG_PRESS_MOVE_TOLERANCE_PX,
  isExcludedTarget = isLongPressExcludedTarget,
  preventDefaultOnStart = false,
}) {
  let timer = null;
  let touchId = null;
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let tracking = false;
  let pressActive = false;
  let suppressClick = false;
  let suppressClickTimer = null;
  let usingPointer = false;
  let captureElement = null;

  function releaseCapture() {
    if (captureElement != null && pointerId != null) {
      try {
        captureElement.releasePointerCapture(pointerId);
      } catch {
        // ignore
      }
    }
    captureElement = null;
  }

  function cancel(reason = "unknown") {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      longPressDebug("timer-cancelled", { reason, tracking, pressActive });
    }
    pressActive = false;
    releaseCapture();
    touchId = null;
    pointerId = null;
    tracking = false;
    usingPointer = false;
  }

  function maybePreventDefault(e) {
    if (preventDefaultOnStart && e.cancelable) {
      e.preventDefault();
    }
  }

  function beginTracking(e, id, clientX, clientY, viaPointer, targetEl) {
    if (tracking) {
      cancel("superseded");
    }

    if (isExcludedTarget(e.target)) {
      return;
    }

    touchId = viaPointer ? null : id;
    pointerId = viaPointer ? id : null;
    usingPointer = viaPointer;
    startX = clientX;
    startY = clientY;
    tracking = true;
    pressActive = true;

    if (viaPointer && targetEl?.setPointerCapture) {
      try {
        targetEl.setPointerCapture(id);
        captureElement = targetEl;
        longPressDebug("pointer-capture-set", { pointerId: id });
      } catch (error) {
        longPressDebug("pointer-capture-failed", {
          pointerId: id,
          message: error?.message || String(error),
        });
      }
    }

    maybePreventDefault(e);
    timer = setTimeout(fireLongPress, durationMs);
    longPressDebug("timer-started", {
      durationMs,
      viaPointer,
      pointerId: id,
    });
  }

  function suppressNextClick() {
    suppressClick = true;
    if (suppressClickTimer) {
      clearTimeout(suppressClickTimer);
    }
    suppressClickTimer = setTimeout(() => {
      suppressClick = false;
      suppressClickTimer = null;
    }, 400);
  }

  function onClickCapture(e) {
    if (!suppressClick) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    suppressClick = false;
    if (suppressClickTimer) {
      clearTimeout(suppressClickTimer);
      suppressClickTimer = null;
    }
  }

  function fireLongPress() {
    timer = null;

    if (!tracking || !pressActive) {
      longPressDebug("fire-blocked", {
        tracking,
        pressActive,
        reason: "released-or-cancelled",
      });
      cancel("fire-blocked");
      return;
    }

    tracking = false;
    pressActive = false;
    releaseCapture();
    touchId = null;
    pointerId = null;
    usingPointer = false;
    suppressNextClick();
    longPressDebug("fire-long-press", { durationMs });
    onLongPress();
  }

  function cancelActiveGesture(reason = "explicit-cancel") {
    cancel(reason);
    suppressClick = false;
    if (suppressClickTimer) {
      clearTimeout(suppressClickTimer);
      suppressClickTimer = null;
    }
  }

  function onTouchStart(e) {
    if (window.PointerEvent) {
      return;
    }

    if (e.touches.length !== 1) {
      return;
    }

    const touch = e.touches[0];
    beginTracking(
      e,
      touch.identifier,
      touch.clientX,
      touch.clientY,
      false,
      e.currentTarget,
    );
  }

  function onTouchMove(e) {
    if (!tracking || usingPointer) {
      return;
    }

    if (e.touches.length > 1) {
      cancel("multi-touch");
      return;
    }

    const touch = findTouch(e.touches, touchId);
    if (!touch) {
      cancel("touch-lost");
      return;
    }

    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;

    if (Math.hypot(deltaX, deltaY) > tolerancePx) {
      cancel("touch-move");
    }
  }

  function onTouchEnd(e) {
    if (usingPointer) {
      return;
    }

    longPressDebug("touch-end", { touchCount: e.touches.length });
    pressActive = false;
    cancel("touch-end");
  }

  function onTouchCancel() {
    if (usingPointer) {
      return;
    }

    longPressDebug("touch-cancel");
    pressActive = false;
    cancel("touch-cancel");
  }

  function onPointerDown(e) {
    if (e.pointerType === "mouse") {
      return;
    }

    if (!e.isPrimary) {
      return;
    }

    longPressDebug("pointer-down", {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
    });
    beginTracking(
      e,
      e.pointerId,
      e.clientX,
      e.clientY,
      true,
      e.currentTarget,
    );
  }

  function onPointerMove(e) {
    if (!tracking || !usingPointer || e.pointerId !== pointerId) {
      return;
    }

    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    if (Math.hypot(deltaX, deltaY) > tolerancePx) {
      cancel("pointer-move");
    }
  }

  function onPointerUp(e) {
    if (!usingPointer || e.pointerId !== pointerId) {
      return;
    }

    longPressDebug("pointer-up", { pointerId: e.pointerId });
    pressActive = false;
    cancel("pointer-up");
  }

  function onPointerCancel(e) {
    if (!usingPointer || e.pointerId !== pointerId) {
      return;
    }

    longPressDebug("pointer-cancel", { pointerId: e.pointerId });
    pressActive = false;
    cancel("pointer-cancel");
  }

  function onLostPointerCapture(e) {
    if (!usingPointer || e.pointerId !== pointerId) {
      return;
    }

    longPressDebug("lost-pointer-capture", { pointerId: e.pointerId });
    pressActive = false;
    cancel("lost-capture");
  }

  function onContextMenu(e) {
    if (isExcludedTarget(e.target)) {
      return;
    }

    e.preventDefault();

    // Touch long-press is owned by the hold timer; ignore contextmenu while tracking.
    if (tracking) {
      longPressDebug("contextmenu-ignored", { tracking, pressActive });
      return;
    }

    longPressDebug("contextmenu-long-press");
    onLongPress();
  }

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture,
    onContextMenu,
    onClickCapture,
    cancelActiveGesture,
    usesPreventDefaultOnStart: preventDefaultOnStart,
  };
}
